import { useMemo } from 'react'
import { essaySummaryStore } from '../model/essaySummaryStore'
import { appAssignmentsStore } from '../model/appAssignmentsStore'
import { sortEssaysByPriority } from '../utils/essayStatusPriority'

const targetKey = row =>
	String(row?.applicationTargetId || row?.id || '').trim()

export const buildRowView = (row, aggregateMap, essaysMap, assignmentsMap) => {
	const key = targetKey(row)
	if (!key) return row

	const essays = sortEssaysByPriority(essaysMap.get(key) || [])
	const aggregateStatus = aggregateMap.get(key) || null
	const feedbackCount = essays.reduce((acc, essay) => {
		const n = Number(essay?.unreadFeedbackCount ?? essay?.unread_feedback_count ?? 0)
		return acc + (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0)
	}, 0)

	return {
		...row,
		essayAggregateStatus: aggregateStatus,
		essays,
		assignments: assignmentsMap.get(key) || [],
		docsUpdatesCount: Number(row?.newDocumentsCount) || 0,
		feedbackCount,
	}
}

export const useApplicationsRowViews = rows => {
	const essayList = essaySummaryStore.essays
	const essayByTarget = essaySummaryStore.byTargetId
	const assignmentList = appAssignmentsStore.assignments

	return useMemo(() => {
		// Tie memo to store snapshots (MobX observer re-renders + eslint exhaustive-deps).
		const storeRevision = [
			essayList.length,
			Object.keys(essayByTarget || {}).length,
			assignmentList.length,
		].join(':')
		void storeRevision

		const aggregateMap = essaySummaryStore.aggregateByTargetId
		const essaysMap = essaySummaryStore.essaysByTargetId
		const assignmentsMap = appAssignmentsStore.assignmentsByTargetId
		const applicationsByTargetId = new Map()
		const views = (rows || []).map(row => {
			const view = buildRowView(row, aggregateMap, essaysMap, assignmentsMap)
			const key = targetKey(view)
			if (key) applicationsByTargetId.set(key, view)
			return view
		})
		return { views, applicationsByTargetId }
	}, [rows, essayList, essayByTarget, assignmentList])
}
